import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthProvider';

const ALLOWED_QUOTE_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
];
const MAX_QUOTE_FILE_BYTES = 10 * 1024 * 1024;

const QuationDetails = ( {orderDetails, onDecline, onSendQuote, isSubmitting, errorMessages} ) => {
  const { t } = useTranslation("orders");
  const { userRole } = useAuth();
  const [estimatedCost, setEstimatedCost] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [quoteFile, setQuoteFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const serviceLine = orderDetails?.order?.additions?.services?.[0];
  const savedQuoteFileUrl = serviceLine?.quote_file_url || null;

  useEffect(() => {
    if (!serviceLine) return;
    if (serviceLine.price != null && serviceLine.price !== '') {
      setEstimatedCost(String(serviceLine.price));
    }
    if (serviceLine.shipping_period != null && serviceLine.shipping_period !== '') {
      setDeliveryTime(String(serviceLine.shipping_period));
    }
    if (serviceLine.supplier_comments) {
      setAdditionalComments(String(serviceLine.supplier_comments));
    }
  }, [serviceLine?.price, serviceLine?.shipping_period, serviceLine?.supplier_comments]);

  const validateForm = () => {
    const errors = {};
    
    if (!estimatedCost || estimatedCost.trim() === '') {
      errors.estimatedCost = t("quotationDetails.validationErrors.estimatedCostRequired");
    } else if (isNaN(parseFloat(estimatedCost)) || parseFloat(estimatedCost) <= 0) {
      errors.estimatedCost = t("quotationDetails.validationErrors.validPositiveNumber");
    } else if (parseFloat(estimatedCost) > 99999999.99) {
      errors.estimatedCost = t("quotationDetails.validationErrors.estimatedCostMaxLimit");
    }

    if (!deliveryTime || deliveryTime.trim() === '') {
      errors.deliveryTime = t("quotationDetails.validationErrors.deliveryTimeRequired");
    } else if (isNaN(parseInt(deliveryTime)) || parseInt(deliveryTime) <= 0) {
      errors.deliveryTime = t("quotationDetails.validationErrors.validPositiveNumber");
    }

    if (quoteFile) {
      if (!ALLOWED_QUOTE_FILE_TYPES.includes(quoteFile.type)) {
        errors.quoteFile = t("quotationDetails.validationErrors.quoteFileType");
      } else if (quoteFile.size > MAX_QUOTE_FILE_BYTES) {
        errors.quoteFile = t("quotationDetails.validationErrors.quoteFileSize");
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time validation for estimated cost (only positive numbers and decimal point)
  const handleEstimatedCostChange = (e) => {
    if (!isReadonly()) {
      const value = e.target.value;
      
      // Allow only numbers and one decimal point
      const numericRegex = /^\d*\.?\d*$/;
      
      if (value === '' || numericRegex.test(value)) {
        setEstimatedCost(value);
        
        // Real-time validation
        if (value === '') {
          setValidationErrors(prev => ({...prev, estimatedCost: t("quotationDetails.validationErrors.estimatedCostRequired")}));
        } else if (parseFloat(value) <= 0) {
          setValidationErrors(prev => ({...prev, estimatedCost: t("quotationDetails.validationErrors.estimatedCostPositive")}));
        } else if (parseFloat(value) > 99999999.99) {
          setValidationErrors(prev => ({...prev, estimatedCost: t("quotationDetails.validationErrors.estimatedCostMaxLimit")}));
        } else {
          setValidationErrors(prev => ({...prev, estimatedCost: ''}));
        }
      }
    }
  };

  // Real-time validation for delivery time (only positive integers)
  const handleDeliveryTimeChange = (e) => {
    if (!isReadonly()) {
      const value = e.target.value;
      
      // Allow only positive integers
      const integerRegex = /^\d*$/;
      
      if (value === '' || integerRegex.test(value)) {
        setDeliveryTime(value);
        
        // Real-time validation
        if (value === '') {
          setValidationErrors(prev => ({...prev, deliveryTime: t("quotationDetails.validationErrors.deliveryTimeRequired")}));
        } else if (parseInt(value) <= 0) {
          setValidationErrors(prev => ({...prev, deliveryTime: t("quotationDetails.validationErrors.deliveryTimePositive")}));
        } else {
          setValidationErrors(prev => ({...prev, deliveryTime: ''}));
        }
      }
    }
  };

  const handleSendQuote = () => {
    if (validateForm()) {
      onSendQuote({
        estimatedCost,
        deliveryTime,
        additionalComments,
        quoteFile,
      });
    }
  };

  const handleQuoteFileChange = (e) => {
    if (isReadonly()) return;
    const file = e.target.files?.[0] || null;
    if (!file) {
      setQuoteFile(null);
      setValidationErrors((prev) => ({ ...prev, quoteFile: '' }));
      return;
    }
    if (!ALLOWED_QUOTE_FILE_TYPES.includes(file.type)) {
      setQuoteFile(null);
      setValidationErrors((prev) => ({
        ...prev,
        quoteFile: t("quotationDetails.validationErrors.quoteFileType"),
      }));
      e.target.value = '';
      return;
    }
    if (file.size > MAX_QUOTE_FILE_BYTES) {
      setQuoteFile(null);
      setValidationErrors((prev) => ({
        ...prev,
        quoteFile: t("quotationDetails.validationErrors.quoteFileSize"),
      }));
      e.target.value = '';
      return;
    }
    setQuoteFile(file);
    setValidationErrors((prev) => ({ ...prev, quoteFile: '' }));
  };

  const isQuoteFileImage = (url) => {
    if (!url) return false;
    const clean = String(url).split('?')[0].toLowerCase();
    return /\.(jpe?g|png|webp)$/.test(clean);
  };

  const isReadonly = () => {
    const status = orderDetails?.order?.status;
    return status === 'accepted' || status === 'rejected' || userRole === 'viewer';
  };

  const getStatusDisplay = () => {
    const status = orderDetails?.order?.status;
    if (status === 'accepted') {
      return (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {t("quotationDetails.statusMessages.quoteAccepted")}
        </div>
      );
    } else if (status === 'rejected') {
      return (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {t("quotationDetails.statusMessages.requestDeclined")}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="quotation-container" style={{ padding: '24px', margin: '0 auto' }}>
      {/* Quotation Details Section */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '24px',
          color: '#1a1a1a'
        }}>
          {t("quotationDetails.quotationHeading")}
        </h2>
        
        {/* Files */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          {orderDetails.order?.additions?.services?.map((service, index) => {
            if (!service.file) return null;
            
            // Determine file type from URL or extension (handle query parameters)
            const fileUrl = service.file;
            const fileName = fileUrl.split('/').pop() || '';
            // Remove query parameters to get clean filename
            const cleanFileName = fileName.split('?')[0];
            const fileExtension = cleanFileName.split('.').pop()?.toLowerCase();
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(fileExtension);
            const isPdf = fileExtension === 'pdf';
            console.log('Rendering file:', fileName, 'Clean filename:', cleanFileName, 'Extension:', fileExtension, 'isImage:', isImage, 'isPdf:', isPdf);
            return (
              <div key={index} style={{
                width: '100px',
                height: '100px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e5e5e5',
                position: 'relative',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onClick={() => window.open(fileUrl, '_blank')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                {isImage ? (
                  <img 
                    src={fileUrl} 
                    alt={`${service.name} ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : isPdf ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff',
                    color: '#dc3545'
                  }}>
                    <svg 
                      width="32" 
                      height="32" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                      style={{ marginBottom: '4px' }}
                    >
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      textAlign: 'center',
                      lineHeight: '1'
                    }}>
                      PDF
                    </span>
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff',
                    color: '#6c757d'
                  }}>
                    <svg 
                      width="32" 
                      height="32" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                      style={{ marginBottom: '4px' }}
                    >
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    <span style={{
                      fontSize: '8px',
                      fontWeight: '600',
                      textAlign: 'center',
                      lineHeight: '1',
                      textTransform: 'uppercase'
                    }}>
                      {fileExtension || 'FILE'}
                    </span>
                  </div>
                )}
                
                {/* Fallback display for failed images */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  color: '#6c757d'
                }}>
                  <svg 
                    width="32" 
                    height="32" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    style={{ marginBottom: '4px' }}
                  >
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    textAlign: 'center',
                    lineHeight: '1'
                  }}>
                    FILE
                  </span>
                </div>

                {/* View overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = 0;
                }}
                >
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="white"
                  >
                    <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quantity */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#464646',
            display: 'block',
            marginBottom: '8px'
          }}>
            {t("quotationDetails.labels.quantity")}
          </label>
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e5e5e5',
            fontSize: '16px',
            color: '#1a1a1a'
          }}>
            {orderDetails.order?.additions?.services[0]?.quantity || 'N/A'}
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#464646',
            display: 'block',
            marginBottom: '8px'
          }}>
            {t("quotationDetails.labels.description")}
          </label>
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e5e5e5',
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#4a4a4a',
            minHeight: '120px'
          }}>
            {orderDetails.order?.additions?.services[0]?.notes || 'No description available'}
          </div>
        </div>
      </div>

      {/* Offer Details Section */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '24px',
          color: '#1a1a1a'
        }}>
          {t("quotationDetails.offerHeading")}
        </h2>
        
        {/* Status Display or Action Buttons */}
        {getStatusDisplay() || (
          userRole !== 'viewer' && (
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              <button
                onClick={onDecline}
                disabled={isSubmitting}
                style={{
                  width: '250px',
                  padding: '8px 12px',
                  backgroundColor: isSubmitting ? '#999' : '#DC3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#c82333';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#dc3545';
                }}
              >
                {t("quotationDetails.buttons.declineRequest")}
              </button>
              <button
                onClick={handleSendQuote}
                disabled={isSubmitting}
                style={{
                  width: '250px',
                  padding: '8px 12px',
                  backgroundColor: isSubmitting ? '#999' : '#198754',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#198754';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#198754';
                }}
              >
                {isSubmitting ? t("quotationDetails.buttons.processing") : t("quotationDetails.buttons.sendQuote")}
              </button>
            </div>
          )
        )}
        </div>

        {/* Error Messages */}
        {errorMessages && errorMessages.length > 0 && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px'
          }}>
            {errorMessages.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </div>
        )}

        {/* Form Fields */}
        {/* Estimated Cost and Delivery Time */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* Estimated Cost */}
          <div>
            <label style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#464646',
              display: 'block',
              marginBottom: '8px'
            }}>
              {t("quotationDetails.labels.estimatedCost")} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              placeholder={t("quotationDetails.placeholders.estimatedCost")}
              value={estimatedCost}
              readOnly={isReadonly()}
              onChange={handleEstimatedCostChange}
              onPaste={(e) => {
                // Prevent pasting non-numeric content
                e.preventDefault();
                const paste = (e.clipboardData || window.clipboardData).getData('text');
                const numericRegex = /^\d*\.?\d*$/;
                if (numericRegex.test(paste)) {
                  handleEstimatedCostChange({ target: { value: paste } });
                }
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                border: `1px solid ${validationErrors.estimatedCost ? '#dc3545' : '#e5e5e5'}`,
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isReadonly() ? '#f5f5f5' : '#fff',
                cursor: isReadonly() ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => {
                if (!isReadonly()) {
                  e.target.style.borderColor = validationErrors.estimatedCost ? '#dc3545' : '#6609F8';
                }
              }}
              onBlur={(e) => {
                if (!isReadonly()) {
                  e.target.style.borderColor = validationErrors.estimatedCost ? '#dc3545' : '#e5e5e5';
                }
              }}
            />
            {validationErrors.estimatedCost && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {validationErrors.estimatedCost}
              </div>
            )}
          </div>

          {/* Estimated Delivery Time */}
          <div>
            <label style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#464646',
              display: 'block',
              marginBottom: '8px'
            }}>
              {t("quotationDetails.labels.estimatedDeliveryTime")} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              placeholder={t("quotationDetails.placeholders.deliveryTime")}
              value={deliveryTime}
              readOnly={isReadonly()}
              onChange={handleDeliveryTimeChange}
              onPaste={(e) => {
                // Prevent pasting non-numeric content
                e.preventDefault();
                const paste = (e.clipboardData || window.clipboardData).getData('text');
                const integerRegex = /^\d*$/;
                if (integerRegex.test(paste)) {
                  handleDeliveryTimeChange({ target: { value: paste } });
                }
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                border: `1px solid ${validationErrors.deliveryTime ? '#dc3545' : '#e5e5e5'}`,
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isReadonly() ? '#f5f5f5' : '#fff',
                cursor: isReadonly() ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => {
                if (!isReadonly()) {
                  e.target.style.borderColor = validationErrors.deliveryTime ? '#dc3545' : '#6609F8';
                }
              }}
              onBlur={(e) => {
                if (!isReadonly()) {
                  e.target.style.borderColor = validationErrors.deliveryTime ? '#dc3545' : '#e5e5e5';
                }
              }}
            />
            {validationErrors.deliveryTime && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {validationErrors.deliveryTime}
              </div>
            )}
          </div>
        </div>

        {/* Additional Comments */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#464646',
            display: 'block',
            marginBottom: '8px'
          }}>
            {t("quotationDetails.labels.additionalComments")} <span style={{ color: '#EF5A29', fontSize: '12px' }}>{t("quotationDetails.labels.optional")}</span>
          </label>
          <textarea
            placeholder={t("quotationDetails.placeholders.additionalComments")}
            value={additionalComments}
            readOnly={isReadonly()}
            onChange={(e) => {
              if (!isReadonly()) {
                setAdditionalComments(e.target.value);
              }
            }}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e5e5e5',
              fontSize: '14px',
              outline: 'none',
              minHeight: '100px',
              resize: isReadonly() ? 'none' : 'vertical',
              fontFamily: 'inherit',
              transition: 'border-color 0.2s',
              backgroundColor: isReadonly() ? '#f5f5f5' : '#fff',
              cursor: isReadonly() ? 'not-allowed' : 'text'
            }}
            onFocus={(e) => {
              if (!isReadonly()) {
                e.target.style.borderColor = '#6609F8';
              }
            }}
            onBlur={(e) => {
              if (!isReadonly()) {
                e.target.style.borderColor = '#e5e5e5';
              }
            }}
          />
        </div>

        {/* Optional quote attachment */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#464646',
            display: 'block',
            marginBottom: '8px'
          }}>
            {t("quotationDetails.labels.quoteFile")}{' '}
            <span style={{ color: '#EF5A29', fontSize: '12px' }}>
              {t("quotationDetails.labels.optional")}
            </span>
          </label>

          {!isReadonly() ? (
            <>
              <input
                type="file"
                accept=".pdf,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                onChange={handleQuoteFileChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${validationErrors.quoteFile ? '#dc3545' : '#e5e5e5'}`,
                  fontSize: '14px',
                  backgroundColor: '#fff',
                }}
              />
              {quoteFile ? (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#4a4a4a' }}>
                  {quoteFile.name}
                </div>
              ) : null}
              {validationErrors.quoteFile ? (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {validationErrors.quoteFile}
                </div>
              ) : (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#888' }}>
                  {t("quotationDetails.placeholders.quoteFileHint")}
                </div>
              )}
            </>
          ) : savedQuoteFileUrl ? (
            <div style={{ marginTop: '4px' }}>
              {isQuoteFileImage(savedQuoteFileUrl) ? (
                <a href={savedQuoteFileUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={savedQuoteFileUrl}
                    alt={t("quotationDetails.labels.quoteFile")}
                    style={{
                      maxWidth: '220px',
                      maxHeight: '160px',
                      borderRadius: '8px',
                      border: '1px solid #e5e5e5',
                      objectFit: 'cover',
                    }}
                  />
                </a>
              ) : (
                <a
                  href={savedQuoteFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    backgroundColor: '#f3eefc',
                    color: '#5507CF',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  {t("quotationDetails.buttons.viewAttachment")}
                </a>
              )}
            </div>
          ) : (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #e5e5e5',
              fontSize: '14px',
              color: '#888',
            }}>
              {t("quotationDetails.labels.noAttachment")}
            </div>
          )}
        </div>

       
      </div>
    </div>
  );
};

export default QuationDetails;